/* Delivery-date estimate for the cart-trust block.
   Liquid only outputs the lead-time day counts (data-attributes) + a text
   fallback; the ACTUAL date is computed here in JS, because Shopify caches
   Liquid output and 'now' would go stale. Implemented as a custom element so it
   re-runs after the cart drawer re-renders via innerHTML (inline <script> would
   not). Guarded so it defines only once. */
if (!customElements.get('cart-delivery-estimate')) {
  class CartDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      /* On a full page load the element can upgrade before its children are
         parsed (script defined before markup) — wait until the output node
         exists so we don't no-op and leave the fallback forever. On innerHTML
         re-renders the subtree is already complete, so render runs immediately. */
      var self = this;
      if (this.querySelector('[data-delivery-output]')) {
        this.render();
      } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { self.render(); }, { once: true });
      } else {
        requestAnimationFrame(function () { self.render(); });
      }
    }

    render() {
      var out = this.querySelector('[data-delivery-output]');
      if (!out) return;
      var min = parseInt(this.dataset.min, 10);
      var max = parseInt(this.dataset.max, 10);
      if (!min || !max || min < 1) return; /* keep the Liquid text fallback */

      var cutoff = parseInt(this.dataset.cutoffHour || '14', 10);
      var locale = this.dataset.locale || document.documentElement.lang || 'en';
      var label = this.dataset.label || '';
      var holidays = (this.dataset.holidays || '')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);

      try {
        var now = new Date();
        var start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        /* Ordered at/after the cutoff hour → counting starts next business day. */
        if (now.getHours() >= cutoff) {
          start = this.addBusinessDays(start, 1, holidays);
        }
        var d1 = this.addBusinessDays(start, min, holidays);
        var d2 = this.addBusinessDays(start, max, holidays);
        var range = this.formatRange(d1, d2, locale);
        out.textContent = label ? label + ' ' + range : range;
      } catch (e) {
        /* leave the server-rendered fallback untouched */
      }
    }

    isBusinessDay(d, holidays) {
      var day = d.getDay();
      if (day === 0 || day === 6) return false;
      var iso =
        d.getFullYear() +
        '-' +
        ('0' + (d.getMonth() + 1)).slice(-2) +
        '-' +
        ('0' + d.getDate()).slice(-2);
      return holidays.indexOf(iso) === -1;
    }

    addBusinessDays(from, n, holidays) {
      var d = new Date(from.getTime());
      var added = 0;
      while (added < n) {
        d.setDate(d.getDate() + 1);
        if (this.isBusinessDay(d, holidays)) added++;
      }
      return d;
    }

    formatRange(d1, d2, locale) {
      var dayMonth = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' });
      /* min == max, or the two dates collapse → a single date, no range */
      if (d1.getTime() === d2.getTime()) {
        return dayMonth.format(d2);
      }
      /* Intl shares the month itself and punctuates per locale:
         da "12.–14. sep." / en-GB "12–14 Sept" / en-US "Sep 12 – 14".
         The old hand-rolled branch concatenated a Danish ordinal "." unconditionally,
         which produced "12.–14 Sept" on the English storefront. */
      if (dayMonth.formatRange) {
        return this.spaceDash(dayMonth.formatRange(d1, d2));
      }
      return this.spaceDash(dayMonth.format(d1) + '–' + dayMonth.format(d2));
    }

    /* Intl's Danish range separator is a tight en dash ("24. sep.–1. okt."), which reads
       cramped wedged between a full stop and a digit. Pad it with thin spaces (U+2009)
       so it breathes without becoming a word of its own, and keep them non-breaking by
       using a narrow no-break space before the dash. Locale-agnostic: en-GB/en-US
       already space their dash, and this just normalises the width. */
    spaceDash(str) {
      return str.replace(/\s*([\u2013\u2014-])\s*/, '\u202F$1\u2009');
    }
  }
  customElements.define('cart-delivery-estimate', CartDeliveryEstimate);
}

/* Reveal the trust block with a slide-up AFTER the cart drawer finishes opening.
   The drawer sets [open], then [active] 75ms later and slides in over ~0.35s, so
   we wait ~430ms and only then add .cart-trust-in (paired with .cart-trust-armed
   in the CSS). This keeps the reveal from playing off-screen during the open. */
(function () {
  if (window.__cartTrustReveal) return;
  window.__cartTrustReveal = true;
  var OPEN_MS = 630;
  function watch(drawer) {
    if (drawer.__trustWatched) return;
    drawer.__trustWatched = true;
    drawer.classList.add('cart-trust-armed');
    var timer;
    var sync = function () {
      clearTimeout(timer);
      if (drawer.hasAttribute('open')) {
        drawer.classList.remove('cart-trust-in');
        timer = setTimeout(function () {
          if (drawer.hasAttribute('open')) drawer.classList.add('cart-trust-in');
        }, OPEN_MS);
      } else {
        drawer.classList.remove('cart-trust-in');
      }
    };
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    sync();
  }
  function init() {
    document.querySelectorAll('cart-drawer').forEach(watch);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
